import React, { useMemo, useState, useEffect } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAppDispatch, useAppSelector } from '../../../state/store';
import { getAllSpaces } from '../../Map/mapHelpers';
import { themeProps } from '../../../styles/theme';
import TagConfigDialog from './TagConfigDialog';
import { setSensorsById } from '../../../state/actions';
import RemoveDialog from '../../Dialog/RemoveDialog';
import useSensorDetailsInLoc from './useSensorDetailsInLoc';
import { getCurrentLocation, getLocationsById } from '../../../state/selectors';
import {
  fetchAllSensorsInLocation,
  fetchSensorLatest,
  updateSensorProperties,
} from '../../../services/apiService';
import { SensorLatest } from '../../../services/api';

function TagList(): JSX.Element {
  const dispatch = useAppDispatch();
  const [selectedTag, setSelectedTag] = useState('');
  const [showTagConfig, setShowTagConfig] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const sensorsDetailsInLoc = useSensorDetailsInLoc();
  const currentLocation = useAppSelector(getCurrentLocation);
  const locationsById = useAppSelector(getLocationsById);

  // fetch tags for location and sublocations
  useEffect(() => {
    fetchAllSensorsInLocation(currentLocation, ['tags'])
      .then((sensors) => {
        const sensorProps = sensors.sensors ?? [];
        dispatch(setSensorsById(sensorProps as SensorLatest[]));
      })
      .catch(() => null);
  }, [currentLocation, dispatch]);

  const allSpaces = useMemo(() => getAllSpaces(sensorsDetailsInLoc), [sensorsDetailsInLoc]);

  const handleDelete = () => {
    const taggedSensors = sensorsDetailsInLoc.filter((sensor) =>
      sensor.tags?.some((item) => item === selectedTag)
    );

    taggedSensors.forEach(async (sensor) => {
      // fetch latest detail
      const sensorDetails = await fetchSensorLatest(sensor.id);
      const updatedTags = sensorDetails?.tags?.filter((tag) => tag !== selectedTag);
      updateSensorProperties(sensor.id, { tags: updatedTags }).then((response) => {
        if (response.id) {
          const updatedSensorDetails = { ...sensor, ...response };
          dispatch(setSensorsById([updatedSensorDetails]));
        }
      });
    });
    setSelectedTag('');
    setShowRemoveDialog(false);
  };

  const getRemoveDialogText = () => {
    let text = '';
    if (selectedTag) {
      const sensorsLength = allSpaces?.find((space) => space.title === selectedTag)?.sensors.length;
      const locName = locationsById.get(currentLocation)?.name;
      text = `${selectedTag} with ${sensorsLength} sensors from ${locName}`;
    }
    return text;
  };

  return (
    <Box>
      <RemoveDialog
        showDialog={showRemoveDialog}
        title={getRemoveDialogText()}
        removeHandler={handleDelete}
        cancelHandler={() => {
          setShowRemoveDialog(false);
          setSelectedTag('');
        }}
      />
      <List>
        <ListItem sx={{ margin: '5px' }}>
          <Grid container>
            <Grid item sm={1} />
            <Grid item sm={4} sx={{ alignSelf: 'center' }}>
              <b>Name</b>
            </Grid>
            <Grid item sm={3} sx={{ alignSelf: 'center' }}>
              <b>No. of Sensors</b>
            </Grid>
            <Grid item sm={3}>
              <Button
                variant={themeProps.btnVariant.secondary}
                onClick={() => setShowTagConfig(true)}
              >
                Add Tag
              </Button>
            </Grid>
          </Grid>
        </ListItem>
        <hr />
        {allSpaces?.map((space) => (
          <ListItem key={space.title} sx={{ margin: '5px' }}>
            <Grid container key={space.title}>
              <Grid item sm={1} />
              <Grid item sm={4}>
                {space.title}
              </Grid>
              <Grid item sm={3}>
                {space.sensors.length}
              </Grid>
              <Grid item sm={3}>
                <IconButton
                  onClick={() => {
                    setShowTagConfig(true);
                    setSelectedTag(space.title);
                  }}
                >
                  <EditIcon color="success" />
                </IconButton>

                <IconButton
                  onClick={() => {
                    setSelectedTag(space.title);
                    setShowRemoveDialog(true);
                  }}
                  sx={{ marginLeft: '5px' }}
                >
                  <DeleteIcon color="error" />
                </IconButton>
              </Grid>
            </Grid>
          </ListItem>
        ))}
      </List>
      {(!allSpaces || allSpaces.length === 0) && (
        <ListItem>
          <Alert severity="error" sx={{ width: '100%' }}>
            No spaces found
          </Alert>
        </ListItem>
      )}
      {showTagConfig && (
        <TagConfigDialog
          showDialog={showTagConfig}
          closeDialog={() => {
            setShowTagConfig(false);
            setSelectedTag('');
          }}
          selectedTag={selectedTag}
          setSelectedTag={setSelectedTag}
          allSpaces={allSpaces}
        />
      )}
    </Box>
  );
}

export default TagList;
